import { useState, useEffect } from "react";
import { DealerAdvantage } from "@/components/shared/DealerAdvantage";
import { SEOHead } from "@/components/shared/SEOHead";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { motion } from "framer-motion";
import { Target, Eye, Award, Users, Signal, DollarSign, Headphones, Smartphone, ChevronLeft, ChevronRight } from "lucide-react";
import { getPageContent } from "@/lib/api";
import { AwardsSection } from "@/components/shared/AwardsSection";
import { WhyChoosePmw } from "@/components/shared/WhyChoosePmw";

const ICON_MAP = { Target, Eye, Award, Users, Signal, DollarSign, Headphones, Smartphone };

const DEFAULTS = {
  hero: { title: "Your Partner in Wireless Success", content: "Prime Media Wireless is a premier Total Wireless Master Agent.", metadata: { subtitle: "About Us" } },
  story: { title: "Our Story", content: "Founded with a vision to empower wireless entrepreneurs...", image_url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80" },
  mission_vision: { metadata: { items: [{ icon: "Target", title: "Our Mission", description: "To empower entrepreneurs..." }, { icon: "Eye", title: "Our Vision", description: "To be the most trusted..." }] } },
  values: { title: "Our Core Values", content: "These principles guide everything we do.", metadata: { items: [] } },
  team: { title: "Our Leadership Team", content: "Experienced professionals dedicated to your success.", metadata: { items: [] } },
};

function getSection(sections, name) {
  const found = sections.find((s) => s.section_name === name && s.active !== false);
  if (!found) return DEFAULTS[name] || {};
  return { ...DEFAULTS[name], ...found, metadata: { ...(DEFAULTS[name]?.metadata || {}), ...(found.metadata || {}) } };
}

export default function AboutPage() {
  const [sections, setSections] = useState([]);
  useEffect(() => { getPageContent("about").then(setSections).catch(() => {}); }, []);

  const hero = getSection(sections, "hero");
  const story = getSection(sections, "story");
  const mv = getSection(sections, "mission_vision");
  const values = getSection(sections, "values");
  const team = getSection(sections, "team");

  const mvItems = mv.metadata?.items || [];
  const valueItems = values.metadata?.items || [];
  const teamItems = team.metadata?.items || [];
  const storyParagraphs = (story.content || "").split("\n").filter(Boolean);
  const carouselImages = story.metadata?.carousel_images?.length ? story.metadata.carousel_images : (story.image_url ? [story.image_url] : []);
  const [carouselIdx, setCarouselIdx] = useState(0);

  useEffect(() => {
    if (carouselImages.length <= 1) return;
    const timer = setInterval(() => setCarouselIdx((p) => (p + 1) % carouselImages.length), 4000);
    return () => clearInterval(timer);
  }, [carouselImages.length]);

  return (
    <PublicLayout>
      <SEOHead
        pageSlug="about"
        fallback={{
          meta_title: "About Prime Media Wireless | Total Wireless Master Agent & Verizon Dealer",
          meta_description: "Learn about Prime Media Wireless — a trusted Total Wireless Master Agent and Verizon Value authorized dealer.",
          canonical_url: `${window.location.origin}/about`,
        }}
      />
      <section className="bg-navy py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="label-style text-teal mb-4">{hero.metadata?.subtitle || "About Us"}</p>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl text-white mb-6">{hero.title}</h1>
            <p className="text-slate-300 text-lg font-body">{hero.content}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl text-navy mb-6">{story.title}</h2>
              <div className="space-y-4 text-slate-600 font-body">
                {storyParagraphs.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              {carouselImages.length > 0 ? (
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-testid="story-carousel">
                  <div className="relative aspect-[4/3]">
                    {carouselImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Our story ${idx + 1}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === carouselIdx % carouselImages.length ? "opacity-100" : "opacity-0"}`}
                      />
                    ))}
                  </div>
                  {carouselImages.length > 1 && (
                    <>
                      <button onClick={() => setCarouselIdx((p) => (p - 1 + carouselImages.length) % carouselImages.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-md transition-colors" data-testid="story-carousel-prev"><ChevronLeft className="w-5 h-5 text-navy" /></button>
                      <button onClick={() => setCarouselIdx((p) => (p + 1) % carouselImages.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-md transition-colors" data-testid="story-carousel-next"><ChevronRight className="w-5 h-5 text-navy" /></button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {carouselImages.map((_, idx) => (
                          <button key={idx} onClick={() => setCarouselIdx(idx)} className={`w-2 h-2 rounded-full transition-all ${idx === carouselIdx % carouselImages.length ? "bg-white w-4" : "bg-white/50"}`} />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </motion.div>
          </div>
        </div>
      </section>

      {mvItems.length > 0 && (
        <section className="py-20 bg-slate-50" data-testid="about-mission">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {mvItems.map((item, index) => {
                const Icon = ICON_MAP[item.icon] || Target;
                return (
                  <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-8 rounded-xl border border-slate-200">
                    <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-6"><Icon className="w-7 h-7 text-teal" /></div>
                    <h3 className="font-heading font-bold text-2xl text-navy mb-4">{item.title}</h3>
                    <p className="text-slate-600 font-body">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {valueItems.length > 0 && (
        <section className="py-20 bg-white" data-testid="about-values">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl text-navy mb-4">{values.title}</h2>
              <p className="text-slate-600 font-body max-w-2xl mx-auto">{values.content}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {valueItems.map((value, index) => {
                const Icon = ICON_MAP[value.icon];
                return (
                  <motion.div key={value.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="text-center p-6">
                    <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      {Icon ? <Icon className="w-6 h-6 text-teal" /> : <span className="text-teal font-bold text-xl">{index + 1}</span>}
                    </div>
                    <h3 className="font-heading font-semibold text-navy text-lg mb-2">{value.title}</h3>
                    <p className="text-slate-600 text-sm font-body">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {teamItems.length > 0 && (
        <section className="py-20 bg-navy" data-testid="about-team">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl text-white mb-4">{team.title}</h2>
            <p className="text-slate-400 font-body max-w-2xl mx-auto mb-12">{team.content}</p>
            <div className="grid md:grid-cols-3 gap-8">
              {teamItems.map((member, index) => (
                <motion.div key={member.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white/5 backdrop-blur border border-white/10 p-6 rounded-xl">
                  <div className="w-20 h-20 bg-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-teal font-heading font-bold text-2xl">{member.name?.split(" ").map((n) => n[0]).join("")}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-white text-lg">{member.name}</h3>
                  <p className="text-teal text-sm font-body">{member.role}</p>
                  <p className="text-slate-500 text-sm font-body mt-1">{member.years}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Awards + Why Choose PMW — anchors the About page with the 2026 Growth Champion recognition */}
      <AwardsSection />
      <DealerAdvantage />
      <WhyChoosePmw />
    </PublicLayout>
  );
}
